import React from "react";
import PortalHeader from "../PortalHeader";
import HistoryView from "./HistoryView";
import StepOne from "./StepOne";
import StepThree from "./StepThree";
import StepTwo from "./StepTwo";
import {
    useRiskAssessmentState,
    type PageView,
} from "./useRiskAssessmentState";

// Re-export for backward compat
export type { C2AssessmentResult } from "../../../types/risk-assessment.types";

const C2RiskAssessment: React.FC = () => {
  const state = useRiskAssessmentState();

  return (
    <div
      style={{
        fontFamily: "system-ui, sans-serif",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f0f4ff 0%, #e8d5ff 50%, #dbeafe 100%)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <PortalHeader onLogout={state.handleLogout} />

      {/* Sub-header bar */}
      <div
        style={{
          background: "linear-gradient(135deg, #1B3A6B 0%, #2E5FA3 50%, #4f46e5 100%)",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          padding: "0 24px",
          display: "flex",
          alignItems: "center",
          height: 48,
          boxShadow: "0 2px 12px rgba(79, 70, 229, 0.25)",
        }}
      >
        <span style={{ fontSize: 13 }}>🛡️</span>
        <span
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: "#ffffff",
            marginLeft: 10,
          }}
        >
          C2 Risk Assessment
        </span>
        <span style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", marginLeft: 8 }}>
          — AUSTRAC Tranche 2
        </span>
      </div>

      {/* ── BODY: sidebar + content ──────────────────────────────────────── */}
      <div className="ra-body-wrapper" style={{ display: "flex", flex: 1 }}>
        {/* Left sidebar */}
        <nav
          className="ra-sidebar"
          style={{
            width: 200,
            background: "linear-gradient(180deg, #1B3A6B 0%, #162d55 100%)",
            borderRight: "1px solid rgba(255,255,255,0.08)",
            padding: "12px 0",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            flexShrink: 0,
            boxShadow: "2px 0 12px rgba(0,0,0,0.15)",
          }}
        >
          {(
            [
              { id: "assessment" as PageView, label: "🛡️ New Assessment" },
              {
                id: "history" as PageView,
                // Show the count only once the first fetch has completed.
                // Rendering history.length before then displays "(0)" for the
                // second or so it takes to connect, authenticate and load, which
                // reads as "you have no assessments" rather than "still loading".
                label: state.historyLoaded
                  ? `📁 History (${state.history.length})`
                  : "📁 History",
              },
            ] as const
          ).map((item) => {
            const isActive = state.pageView === item.id;
            return (
              <button
                key={item.id}
                onClick={() => state.setPageView(item.id)}
                style={{
                  background: isActive
                    ? "rgba(59,130,246,0.18)"
                    : "transparent",
                  color: isActive ? "#93C5FD" : "#94A3B8",
                  border: "none",
                  borderLeft: `3px solid ${isActive ? "#3B82F6" : "transparent"}`,
                  padding: "10px 16px",
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: isActive ? 600 : 400,
                  textAlign: "left",
                  width: "100%",
                  transition: "background 0.15s",
                }}
              >
                {item.label}
              </button>
            );
          })}
        </nav>

        {/* Main content */}
        <div style={{ flex: 1, overflow: "auto" }}>
          {/* ── HISTORY VIEW ── */}
          {state.pageView === "history" && (
            <HistoryView
              history={state.history}
              reset={state.reset}
              setPageView={state.setPageView}
              deleteHistoryEntry={state.deleteHistoryEntry}
            />
          )}

          {/* ── ASSESSMENT VIEW ── */}
          {state.pageView === "assessment" && (
            <main
              style={{
                flex: 1,
                padding: 24,
                maxWidth: 860,
                margin: "0 auto",
                width: "100%",
              }}
            >
              <div>
                {/* Header */}
                <div style={{ marginBottom: 24 }}>
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 7,
                      background: "#EFF6FF",
                      border: "1px solid #BFDBFE",
                      borderRadius: 20,
                      padding: "3px 12px",
                      marginBottom: 10,
                    }}
                  >
                    <div
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: "#1B3A6B",
                      }}
                    />
                    <span
                      style={{
                        color: "#1B3A6B",
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                      }}
                    >
                      ARCaml · AML/CTF Platform · Tranche 2
                    </span>
                  </div>
                  <h1
                    style={{
                      color: "#1E293B",
                      fontSize: 22,
                      fontWeight: 800,
                      margin: "0 0 5px",
                      letterSpacing: "-0.4px",
                    }}
                  >
                    {state.editingId
                      ? "✏️ Editing C2 Risk Assessment"
                      : "C2 Risk Assessment Questionnaire"}
                  </h1>
                  {state.editingId && (
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        background: "#FFF7ED",
                        border: "1px solid #FED7AA",
                        borderRadius: 8,
                        padding: "4px 12px",
                        marginBottom: 6,
                      }}
                    >
                      <span style={{ fontSize: 11 }}>📝</span>
                      <span
                        style={{
                          color: "#C2410C",
                          fontSize: 11,
                          fontWeight: 600,
                        }}
                      >
                        Editing existing assessment for {state.c2Name}
                      </span>
                      <button
                        onClick={() => state.reset()}
                        style={{
                          background: "transparent",
                          border: "none",
                          color: "#94A3B8",
                          cursor: "pointer",
                          fontSize: 11,
                          marginLeft: 4,
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  <p style={{ color: "#64748B", fontSize: 12, margin: 0 }}>
                    Questions sourced directly from AUSTRAC Initial CDD Forms —
                    January 2026. Your C1 profession type is auto-detected from
                    your organisation profile.
                  </p>
                </div>

                {/* Progress */}
                <div style={{ marginBottom: 22 }}>
                  <div style={{ display: "flex", gap: 4, marginBottom: 5 }}>
                    {["Setup", "Questionnaire", "Result"].map((lbl, i) => (
                      <div
                        key={i}
                        style={{
                          flex: 1,
                          height: 3,
                          borderRadius: 3,
                          background:
                            state.step > i
                              ? "linear-gradient(90deg,#1B3A6B,#2E5FA3)"
                              : "#E2E8F0",
                          transition: "background 0.3s",
                        }}
                      />
                    ))}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    {["Setup", "Questionnaire", "Result"].map((lbl, i) => (
                      <span
                        key={i}
                        style={{
                          fontSize: 10,
                          color: state.step > i ? "#1B3A6B" : "#94A3B8",
                          fontWeight: state.step === i + 1 ? 700 : 400,
                        }}
                      >
                        {lbl}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Card */}
                <div
                  style={{
                    background: "linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)",
                    border: "1px solid #bfdbfe",
                    borderRadius: 16,
                    overflow: "hidden",
                    boxShadow: "0 8px 32px rgba(79, 70, 229, 0.12)",
                    position: "relative",
                  }}
                >
                  {/* Top gradient stripe */}
                  <div style={{
                    position: "absolute",
                    top: 0, left: 0, right: 0,
                    height: 3,
                    background: "linear-gradient(90deg, #1B3A6B, #4f46e5, #7c3aed)",
                  }} />
                  {/* Card topbar */}
                  <div
                    style={{
                      padding: "13px 24px",
                      paddingTop: 16,
                      borderBottom: "1px solid #dbeafe",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      background: "linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%)",
                    }}
                  >
                    <div
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: 7,
                        background: "linear-gradient(135deg,#1B3A6B,#2E5FA3)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 12,
                      }}
                    >
                      🛡️
                    </div>
                    <div>
                      <div
                        style={{
                          color: "#1E293B",
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        {state.step === 1
                          ? "Step 1 — C2 Customer Details"
                          : state.step === 2
                            ? `Step 2 — Section A1 Risk Factors · ${state.c1Label} · ${state.c2Name?.trim() || state.selC2?.label}`
                            : "Step 3 — Preliminary Risk Rating"}
                      </div>
                      {state.step === 2 && (
                        <div
                          style={{
                            color: "#94A3B8",
                            fontSize: 10,
                            marginTop: 1,
                          }}
                        >
                          {state.questions.length} risk factor questions ·
                          Official AUSTRAC CDD Form (Jan 2026)
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ── STEP 1 ── */}
                  {state.step === 1 && (
                    <StepOne
                      c1Type={state.c1Type}
                      setC1Type={state.setC1Type}
                      c2Name={state.c2Name}
                      setC2Name={state.setC2Name}
                      c2EntityType={state.c2EntityType}
                      setC2EntityType={state.setC2EntityType}
                      isC2Individual={state.isC2Individual}
                      setIsC2Individual={state.setIsC2Individual}
                      entitySubType={state.entitySubType}
                      setEntitySubType={state.setEntitySubType}
                      c2Abn={state.c2Abn}
                      setC2Abn={state.setC2Abn}
                      abnCodeType={state.abnCodeType}
                      abnLookupResult={state.abnLookupResult}
                      abnLookupError={state.abnLookupError}
                      isLookingUp={state.isLookingUp}
                      handleAbnChange={state.handleAbnChange}
                      handleAbnCodeTypeChange={state.handleAbnCodeTypeChange}
                      triggerKybLookup={state.triggerKybLookup}
                      setAnswers={state.setAnswers}
                      setAbnLookupResult={state.setAbnLookupResult}
                      setAbnLookupError={state.setAbnLookupError}
                      setStep={state.setStep}
                      contactFirst={state.contactFirst}
                      setContactFirst={state.setContactFirst}
                      contactMiddle={state.contactMiddle}
                      setContactMiddle={state.setContactMiddle}
                      contactLast={state.contactLast}
                      setContactLast={state.setContactLast}
                      contactEmail={state.contactEmail}
                      setContactEmail={state.setContactEmail}
                      contactPhone={state.contactPhone}
                      setContactPhone={state.setContactPhone}
                    />
                  )}

                  {/* ── STEP 2 ── */}
                  {state.step === 2 && (
                    <StepTwo
                      grouped={state.grouped}
                      questions={state.questions}
                      answers={state.answers}
                      setAnswers={state.setAnswers}
                      answered={state.answered}
                      allDone={state.allDone}
                      activeSection={state.activeSection}
                      setActiveSection={state.setActiveSection}
                      liveRisk={state.liveRisk}
                      liveRc={state.liveRc}
                      hoveredInfo={state.hoveredInfo}
                      setHoveredInfo={state.setHoveredInfo}
                      setStep={state.setStep}
                    />
                  )}

                  {/* ── STEP 3 ── */}
                  {state.step === 3 && state.rc && state.risk && (
                    <StepThree
                      risk={state.risk}
                      rc={state.rc}
                      c1Label={state.c1Label}
                      c2Name={state.c2Name}
                      c2EntityType={state.c2EntityType}
                      selC2={state.selC2}
                      flagged={state.flagged}
                      questions={state.questions}
                      reset={state.reset}
                      handleComplete={state.handleComplete}
                      isCompleting={state.isCompleting}
                    />
                  )}
                </div>

                <p
                  style={{
                    color: "#94A3B8",
                    fontSize: 11,
                    textAlign: "center",
                    marginTop: 18,
                  }}
                >
                  ARCaml · AML/CTF Compliance Platform · AUSTRAC Tranche 2
                  Reform · January 2026
                </p>
              </div>
            </main>
          )}
        </div>
        {/* end main content */}
      </div>
      {/* end body wrapper */}

      {/* Footer */}
      <footer
        style={{
          background: "linear-gradient(135deg, #1B3A6B 0%, #162d55 100%)",
          color: "rgba(255,255,255,0.4)",
          padding: "10px 24px",
          fontSize: 11,
          display: "flex",
          justifyContent: "space-between",
          borderTop: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <span>ARCaml — iDeedworks Pty Ltd © 2026</span>
        <span>Based on AUSTRAC Tranche 2 Guidance — Effective 1 July 2026</span>
      </footer>
    </div>
  );
};

export default C2RiskAssessment;
